import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { StatusBadge, currency } from "@/components/Primitives";
import { ROLE_LABELS } from "@/config/tools";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, BedDouble, Bath, Ruler, TrendingUp, FileSignature, Megaphone,
  CheckCircle2, Circle, Lock, MapPin,
} from "lucide-react";

const DEAL_TONE = { "New Lead": "gray", Contacted: "amber", "Appointment Set": "orange", "Offer Made": "orange", Won: "green", Lost: "red" };
const CONTRACT_TONE = { "Under Contract": "orange", Pending: "amber", Closed: "green", Cancelled: "red" };
const LISTING_TONE = { Active: "green", Draft: "gray", "Under Contract": "orange", Sold: "emerald" };

function Section({ icon: Icon, title, hasAccess, children }) {
  return (
    <div className="bg-white border border-[#E5E5E2] rounded-sm">
      <div className="px-6 py-4 border-b border-[#E5E5E2] flex items-center gap-2">
        <Icon className="w-5 h-5 text-[#F7941D]" strokeWidth={1.5} />
        <h3 className="font-serif text-xl">{title}</h3>
      </div>
      <div className="p-6">
        {hasAccess ? children : (
          <div className="flex items-center gap-2 text-sm text-[#8a8a86]">
            <Lock className="w-4 h-4" strokeWidth={1.5} /> Your role doesn't have access to this section.
          </div>
        )}
      </div>
    </div>
  );
}

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/properties/${id}`).then((r) => setData(r.data)); }, [id]);

  if (!data) return null;
  const { property: p, access } = data;

  return (
    <div data-testid="property-detail-page">
      <button data-testid="back-to-properties" onClick={() => navigate("/properties")} className="inline-flex items-center gap-2 text-sm text-[#575755] hover:text-[#1F3C88] transition-colors duration-200 mb-6">
        <ArrowLeft className="w-4 h-4" /> All properties
      </button>

      {/* Header */}
      <div className="bg-white border border-[#E5E5E2] rounded-sm overflow-hidden mb-8 animate-fade-up">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="h-56 lg:h-auto bg-[#F0EFEC]">
            {p.image ? <img src={p.image} alt={p.address} className="w-full h-full object-cover" /> : null}
          </div>
          <div className="p-8">
            <div className="flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-[#575755]"><MapPin className="w-4 h-4 text-[#F7941D]" />{p.city}</div>
            <h1 className="mt-2 font-serif text-4xl tracking-tight text-[#0A0A0A]">{p.address}</h1>
            <div className="mt-2 font-serif text-3xl text-[#1F3C88]">{currency(p.price)}</div>
            <div className="flex items-center gap-6 mt-5 text-sm text-[#575755]">
              <span className="flex items-center gap-1.5"><BedDouble className="w-4 h-4" strokeWidth={1.5} />{p.beds} beds</span>
              <span className="flex items-center gap-1.5"><Bath className="w-4 h-4" strokeWidth={1.5} />{p.baths} baths</span>
              <span className="flex items-center gap-1.5"><Ruler className="w-4 h-4" strokeWidth={1.5} />{p.sqft?.toLocaleString()} sqft</span>
            </div>
            <div className="mt-6 text-xs text-[#8a8a86]">Viewing as <span className="text-[#1F3C88] font-medium">{ROLE_LABELS[user.role]}</span> — you see the sections your role can access.</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales */}
        <Section icon={TrendingUp} title="Sales Activity" hasAccess={access.includes("sales")}>
          {data.deals?.length ? data.deals.map((d) => (
            <div key={d.id} className="mb-4 last:mb-0">
              <div className="flex items-center justify-between"><span className="font-medium text-[#0A0A0A]">{d.client_name}</span><StatusBadge tone={DEAL_TONE[d.stage]}>{d.stage}</StatusBadge></div>
              <div className="text-sm text-[#575755] mt-1">{d.agent} · {d.source}</div>
              <div className="text-sm font-medium text-[#1F3C88] mt-1">{currency(d.price)}</div>
            </div>
          )) : <div className="text-sm text-[#8a8a86]">No deals linked to this property.</div>}
        </Section>

        {/* Contract */}
        <Section icon={FileSignature} title="Contract Status" hasAccess={access.includes("transactions")}>
          {data.transactions?.length ? data.transactions.map((t) => (
            <div key={t.id}>
              <div className="flex items-center justify-between"><span className="font-medium text-[#0A0A0A]">{t.buyer} ← {t.seller}</span><StatusBadge tone={CONTRACT_TONE[t.status]}>{t.status}</StatusBadge></div>
              <div className="text-sm text-[#575755] mt-1">Closes {t.closing_date || "TBD"}</div>
              <div className="mt-4 space-y-2">
                {t.checklist?.map((c, i) => (
                  <div key={i} className="flex items-center gap-2">
                    {c.done ? <CheckCircle2 className="w-4 h-4 text-[#2E5C47]" strokeWidth={1.5} /> : <Circle className="w-4 h-4 text-[#B9B5AC]" strokeWidth={1.5} />}
                    <span className={`text-sm ${c.done ? "text-[#8a8a86] line-through" : "text-[#0A0A0A]"}`}>{c.task}</span>
                  </div>
                ))}
              </div>
            </div>
          )) : <div className="text-sm text-[#8a8a86]">No active contract on this property.</div>}
        </Section>

        {/* Marketing */}
        <Section icon={Megaphone} title="Marketing" hasAccess={access.includes("listings")}>
          {data.listing ? (
            <div>
              <div className="flex items-center justify-between"><span className="font-medium text-[#0A0A0A]">Listing</span><StatusBadge tone={LISTING_TONE[data.listing.status] || "gray"}>{data.listing.status}</StatusBadge></div>
              <div className="mt-2 flex items-center justify-between text-sm"><span className="text-[#575755]">Campaign</span><StatusBadge tone={data.listing.campaign_status === "Live" ? "green" : data.listing.campaign_status === "Scheduled" ? "amber" : "gray"}>{data.listing.campaign_status}</StatusBadge></div>
              <div className="mt-4 text-xs tracking-[0.15em] uppercase text-[#575755] mb-2">Channels</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(data.listing.channels || {}).map(([k, v]) => (
                  <span key={k} className={`px-2.5 py-1 rounded-full text-xs capitalize ${v ? "bg-[#E7F0EA] text-[#2E5C47]" : "bg-[#F0EFEC] text-[#8a8a86]"}`}>{k}{v ? " ✓" : ""}</span>
                ))}
              </div>
            </div>
          ) : <div className="text-sm text-[#8a8a86]">Not currently listed / advertised.</div>}
        </Section>
      </div>
    </div>
  );
}
